/**
 * CountryDetailPage — /app/macro/:code
 *
 * Deep-dive on a single African exchange / country. All data live from
 * Mansa (5-min server cache). Layout:
 *
 *   [Header]  Exchange name · Country · Trading hours · Currency
 *             Index name/value/change · YTD · Market cap · Session status
 *
 *   [Central bank widget]  Policy rate · Inflation YoY · Source · Last update
 *
 *   [Top holdings table]  Market-cap-descending listed stocks (20)
 *
 *   [Gainers/Losers]     Two-column top-mover cards
 *
 * The country policy-rate row is looked up by 2-letter ISO country code
 * (Mansa's `country` field is the ISO alpha-2, e.g. "KE"). We resolve the
 * exchange → country mapping via the exchange_detail payload.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
    ChevronLeft, TrendingUp, TrendingDown, Landmark,
    Percent, Activity, Building2, Globe, ArrowLeft,
} from "lucide-react";
import { EXCHANGE_INFO } from "@/data/africaCountryPaths";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";

const ISO_ALPHA2_BY_NAME = {
    "Nigeria": "NG", "Ghana": "GH", "Kenya": "KE", "South Africa": "ZA",
    "Egypt": "EG", "Morocco": "MA", "Côte d'Ivoire": "CI", "Ivory Coast": "CI",
    "Tanzania": "TZ", "Zambia": "ZM", "Uganda": "UG", "Rwanda": "RW",
    "Malawi": "MW", "Zimbabwe": "ZW", "Botswana": "BW", "Namibia": "NA",
    "Mauritius": "MU", "Tunisia": "TN", "Algeria": "DZ", "Cape Verde": "CV",
    "Eswatini": "SZ", "Angola": "AO", "Cameroon": "CM",
};

/* Map exchange codes → ISO country codes for back-reference */
const EXCHANGE_TO_ISO = {
    NSE: "KE", NGX: "NG", JSE: "ZA", EGX: "EG", CSE: "MA",
    BRVM: "CI", BVMAC: "CM", GSE: "GH", DSE: "TZ", USE: "UG",
    RSE: "RW", ESX: "ET", LuSE: "ZM", NSX: "NA", SEM: "MU",
    MSE: "MW", ZSE: "ZW", BSE: "BW", ESE: "SZ", BODIVA: "AO",
    BVMT: "TN", SGBV: "DZ",
};

export default function CountryDetailPage() {
    const { code } = useParams();
    const [detail, setDetail] = useState(null);
    const [holdings, setHoldings] = useState([]);
    const [gainers, setGainers] = useState([]);
    const [losers, setLosers] = useState([]);
    const [policyRates, setPolicyRates] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!code) return;
        setLoading(true);

        const timeout = setTimeout(() => setLoading(false), 8000);

        Promise.all([
            api.get(`/market/live/exchange/${code}`).then(r => r.data?.exchange || null).catch(() => null),
            api.get(`/market/live/exchange/${code}/stocks?limit=20`).then(r => r.data?.items || []).catch(() => []),
            api.get(`/market/live/exchange/${code}/movers?direction=gainers&limit=8`).then(r => r.data?.items || []).catch(() => []),
            api.get(`/market/live/exchange/${code}/movers?direction=losers&limit=8`).then(r => r.data?.items || []).catch(() => []),
            api.get(`/market/live/macro/policy-rates`).then(r => r.data?.items || []).catch(() => []),
            api.get(`/market/live/exchange/${code}/exchange-metadata`).then(r => r.data).catch(() => null),
        ]).then(([d, h, g, l, pr, meta]) => {
            setDetail(d);
            setHoldings(h);
            setGainers(g);
            setLosers(l);
            setPolicyRates(pr);
            setMetadata(meta);
        }).finally(() => { clearTimeout(timeout); setLoading(false); });

        return () => clearTimeout(timeout);
    }, [code]);

    /* Local exchange data for fallback display */
    const localExchange = EXCHANGE_INFO?.[code] || null;
    const countryIso = detail
        ? (ISO_ALPHA2_BY_NAME[detail.country] || EXCHANGE_TO_ISO[code] || null)
        : (EXCHANGE_TO_ISO[code] || null);
    const policyRow = useMemo(
        () => policyRates.find((r) => r.country === countryIso) || null,
        [policyRates, countryIso],
    );
    const { sorted: sortedHoldings, sortKey, direction, requestSort } = useSortableData(holdings, {
        accessors: {
            ticker: (h) => h.ticker || "",
            name: (h) => h.name || "",
            price: (h) => Number(h.price || 0),
            change: (h) => Number(h.change_pct || 0),
            volume: (h) => Number(h.volume || 0),
        },
    });

    if (loading) {
        return (
            <div className="p-8 space-y-4">
                <Link to="/app" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
                    <ChevronLeft className="w-3 h-3" /> Back to dashboard
                </Link>
                <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-muted-foreground">Loading {localExchange?.fullName || code} data…</span>
                </div>
            </div>
        );
    }
    if (!detail) {
        return (
            <div className="p-6 space-y-5" data-testid="country-detail-empty">
                <Link to="/app" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="country-back-link">
                    <ChevronLeft className="w-3 h-3" /> Back to dashboard
                </Link>

                {localExchange ? (
                    <div className="space-y-5">
                        <div className="border border-border bg-card rounded-xl p-5">
                            <div className="flex items-start justify-between flex-wrap gap-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-primary mb-1">Exchange</div>
                                    <h1 className="text-3xl font-bold">{localExchange.fullName}</h1>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {localExchange.code} &middot; Currency: {localExchange.currency}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">{countryIso || code}</span>
                                </div>
                            </div>
                        </div>
                        <div className="border border-border/60 bg-card/60 rounded-xl p-5">
                            <div className="flex items-center gap-3 mb-3">
                                <Activity className="w-5 h-5 text-yellow-500" />
                                <h2 className="text-base font-semibold">Live Data Temporarily Unavailable</h2>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                Real-time market data for <strong>{localExchange.fullName}</strong> ({localExchange.code}) is
                                temporarily unavailable. Stocks, movers, and index data will appear here once the feed reconnects.
                            </p>
                            <div className="mt-4 flex gap-3 flex-wrap">
                                <Link to="/app" className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-border hover:bg-secondary transition-colors">
                                    <ArrowLeft className="w-3 h-3" /> Return to dashboard
                                </Link>
                                <button onClick={() => window.location.reload()} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                                    Retry
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="text-sm text-muted-foreground mb-3">Exchange &ldquo;{code}&rdquo; has no coverage.</div>
                        <Link to="/app" className="text-primary underline text-sm">&larr; Back to dashboard</Link>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="p-6 space-y-5" data-testid="country-detail-page">
            <Link
                to="/app"
                className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
                data-testid="country-back-link"
            >
                <ChevronLeft className="w-3 h-3" /> Back to dashboard
            </Link>

            {/* Header */}
            <div className="border border-border bg-card rounded-xl p-5" data-testid="country-header">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-primary mb-1">Country · Exchange</div>
                        <h1 className="text-3xl font-bold" data-testid="country-title">
                            {detail.country}
                        </h1>
                        <div className="text-sm text-muted-foreground">
                            {detail.name}
                            {detail.trading_hours && <> · {detail.trading_hours}</>}
                            {detail.timezone && <> · {detail.timezone}</>}
                        </div>
                    </div>
                    <div className="flex gap-6 text-right">
                        {detail.index_name && (
                            <div>
                                <div className="text-[11px] uppercase text-muted-foreground">{detail.index_name}</div>
                                <div className="proportional-nums font-bold text-2xl" data-testid="country-index-value">
                                    {Number(detail.index_value || 0).toLocaleString()}
                                </div>
                                <div className={"text-sm mono " + toneClassName(signTone(detail.index_change_pct || 0))}>
                                    {signedPrefix(detail.index_change_pct || 0)}{Number(detail.index_change_pct || 0).toFixed(2)}%
                                </div>
                            </div>
                        )}
                        {detail.market_cap && (
                            <div>
                                <div className="text-[11px] uppercase text-muted-foreground">Market cap</div>
                                <div className="proportional-nums font-semibold">{detail.market_cap}</div>
                            </div>
                        )}
                        {detail.ytd_change && (
                            <div>
                                <div className="text-[11px] uppercase text-muted-foreground">YTD</div>
                                <div className="proportional-nums font-semibold">{detail.ytd_change}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Exchange metadata — descriptive info from Mansa's exchange-status cache */}
            {metadata?.available && (
                <div className="border border-border bg-card rounded-xl p-4" data-testid="country-exchange-metadata">
                    <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-4 h-4 text-primary" />
                        <div className="overline">Exchange metadata</div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        {Object.entries(metadata)
                            .filter(([k, v]) => !["available", "_id", "code", "name"].includes(k) && v !== null && v !== undefined && typeof v !== "object")
                            .map(([k, v]) => (
                                <div key={k}>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
                                    <div className="font-semibold proportional-nums">{String(v)}</div>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {/* Central-bank / macro widget */}
            <div className="border border-border bg-card rounded-xl p-4" data-testid="country-central-bank">
                <div className="flex items-center gap-2 mb-3">
                    <Landmark className="w-4 h-4 text-primary" />
                    <div className="overline">Central bank &amp; inflation</div>
                    {policyRow?.source_institution && (
                        <span className="text-[10px] text-muted-foreground ml-auto truncate max-w-[50%]"
                              title={policyRow.source_institution}>
                            via {policyRow.source_institution}
                        </span>
                    )}
                </div>
                {!policyRow ? (
                    <div className="text-xs text-muted-foreground">
                        No central-bank data yet for {detail.country}. Free-tier Mansa
                        currently covers 18 African economies.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Percent className="w-3 h-3" /> Policy rate</div>
                            <div className="proportional-nums font-bold text-2xl" data-testid="country-policy-rate">
                                {policyRow.policy_rate}%
                            </div>
                            <div className="text-[11px] text-muted-foreground">{policyRow.policy_rate_period}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Activity className="w-3 h-3" /> Inflation YoY</div>
                            <div className="proportional-nums font-bold text-2xl" data-testid="country-inflation">
                                {policyRow.inflation_yoy}%
                            </div>
                            <div className="text-[11px] text-muted-foreground">{policyRow.inflation_period}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-muted-foreground">Real rate</div>
                            <div className="proportional-nums font-bold text-2xl">
                                {(policyRow.policy_rate - policyRow.inflation_yoy).toFixed(1)}%
                            </div>
                            <div className="text-[11px] text-muted-foreground">Policy − CPI</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase text-muted-foreground">Data mode</div>
                            <div className="text-sm font-semibold mt-1">{policyRow.data_mode || "live"}</div>
                            {policyRow.notes && (
                                <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2" title={policyRow.notes}>
                                    {policyRow.notes}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Top holdings + movers */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border bg-card rounded-xl p-4" data-testid="country-holdings">
                    <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-4 h-4 text-primary" />
                        <div className="overline">Listed holdings · {detail.currency}</div>
                        <span className="text-[10px] text-muted-foreground ml-auto">
                            {holdings.length} of {detail.total_stocks || "—"}
                        </span>
                    </div>
                    {holdings.length === 0 ? (
                        <div className="text-xs text-muted-foreground">No listing data available.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs text-muted-foreground">
                                <tr>
                                    <SortableTh label="Ticker" sortKey="ticker" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-1" />
                                    <SortableTh label="Name" sortKey="name" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-1" />
                                    <SortableTh label="Price" sortKey="price" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="py-1" />
                                    <SortableTh label="Change" sortKey="change" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="py-1" />
                                    <SortableTh label="Volume" sortKey="volume" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="py-1" />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedHoldings.map((h) => (
                                    <tr key={h.ticker} className="border-b border-border/40" data-testid={`holding-${h.ticker}`}>
                                        <td className="mono font-semibold py-1.5">{h.ticker}</td>
                                        <td className="text-xs text-muted-foreground truncate max-w-[220px]">{h.name}</td>
                                        <td className="text-right proportional-nums font-medium">{Number(h.price || 0).toLocaleString()}</td>
                                        <td className={"text-right proportional-nums text-xs " + toneClassName(signTone(h.change_pct || 0))}>
                                            {signedPrefix(h.change_pct || 0)}{Number(h.change_pct || 0).toFixed(2)}%
                                        </td>
                                        <td className="text-right proportional-nums text-xs text-muted-foreground">
                                            {h.volume ? Number(h.volume).toLocaleString() : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="space-y-4">
                    <div className="border border-border bg-card rounded-xl p-4" data-testid="country-gainers">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                            <div className="overline">Top gainers</div>
                        </div>
                        {gainers.length === 0
                            ? <div className="text-xs text-muted-foreground">—</div>
                            : gainers.map((m) => (
                                <div key={m.ticker} className="flex justify-between py-1 text-sm border-b border-border/30 last:border-0">
                                    <span className="mono font-semibold">{m.ticker}</span>
                                    <span className={`${toneClassName(signTone(m.change_pct || 0))} proportional-nums text-xs`}>{signedPrefix(m.change_pct || 0)}{Number(m.change_pct || 0).toFixed(2)}%</span>
                                </div>
                            ))}
                    </div>
                    <div className="border border-border bg-card rounded-xl p-4" data-testid="country-losers">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingDown className="w-4 h-4 text-rose-500" />
                            <div className="overline">Top losers</div>
                        </div>
                        {losers.length === 0
                            ? <div className="text-xs text-muted-foreground">—</div>
                            : losers.map((m) => (
                                <div key={m.ticker} className="flex justify-between py-1 text-sm border-b border-border/30 last:border-0">
                                    <span className="mono font-semibold">{m.ticker}</span>
                                    <span className={`${toneClassName(signTone(m.change_pct || 0))} proportional-nums text-xs`}>{signedPrefix(m.change_pct || 0)}{Number(m.change_pct || 0).toFixed(2)}%</span>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
